import { Badge, Button, Card } from '@/design-system';
import type { TemplateSummary } from '@/contracts/templates';

export interface TemplateCardProps {
  template: TemplateSummary;
  onPreview: (template: TemplateSummary) => void;
  onUse: (template: TemplateSummary) => void;
  onEdit?: (template: TemplateSummary) => void;
  onUpdateFromFunnel?: (template: TemplateSummary) => void;
  onDelete?: (template: TemplateSummary) => void;
}
const CATEGORY_LABELS: Record<string, string> = {
  agency: 'Agência',
  b2b: 'B2B',
  health: 'Saúde',
  commerce: 'Comércio eletrônico',
  'local-business': 'Negócio local',
  'digital-products': 'Produtos digitais',
};

/** SPEC-010 §25/AC-019 — required card metadata: name/description/category/tags/badge/preview. */
export function TemplateCard({ template, onPreview, onUse, onEdit, onUpdateFromFunnel, onDelete }: TemplateCardProps) {
  const isCustom = template.type === 'custom';
  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">{template.name}</h3>
          <p className="mt-1 text-xs text-text-muted">{CATEGORY_LABELS[template.category] ?? template.category}</p>
        </div>
        <Badge>{isCustom ? 'Personalizado' : 'Oficial'}</Badge>
      </div>

      <p className="line-clamp-2 text-sm text-text-secondary">{template.description}</p>

      {template.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {template.tags.map((tag) => (
            <Badge key={tag}>{tag}</Badge>
          ))}
        </div>
      ) : null}

      <p className="text-xs text-text-muted">
        {template.previewMetadata.nodeCount} etapas · {template.previewMetadata.edgeCount} conexões
      </p>

      <div className="mt-auto flex flex-wrap gap-2 pt-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => onPreview(template)}
          aria-label={`Pré-visualizar ${template.name}`}
        >
          Pré-visualizar
        </Button>
        <Button type="button" onClick={() => onUse(template)} aria-label={`Usar ${template.name}`}>
          Usar modelo
        </Button>
        {isCustom && onEdit ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => onEdit(template)}
            aria-label={`Editar metadados de ${template.name}`}
          >
            Editar
          </Button>
        ) : null}
        {isCustom && onUpdateFromFunnel ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => onUpdateFromFunnel(template)}
            aria-label={`Atualizar ${template.name} a partir de um funil`}
          >
            Atualizar a partir do funil
          </Button>
        ) : null}
        {isCustom && onDelete ? (
          <Button
            type="button"
            variant="danger"
            onClick={() => onDelete(template)}
            aria-label={`Excluir ${template.name}`}
          >
            Excluir
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
